import { useApolloClient } from '@apollo/client'
import { Loader, TriangleAlert } from 'lucide-react'
import { use, useTransition } from 'react'
import { toast } from 'sonner'
import { CompanySuspensionContext } from '@/components/company-suspension/context'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany } from '@/lib/hooks'
import type {
  PaymentSuspensionStripePortalQuery,
  PaymentSuspensionStripePortalQueryVariables,
} from '@/components/layout/payment-suspension-notice/index.types.gen'

const PAYMENT_SUSPENSION_STRIPE_PORTAL_QUERY = gql`
  query PaymentSuspensionStripePortal($idCompany: String!) {
    company(id: $idCompany) {
      id
      sevallaBilling {
        id
        stripeCustomerPortal
      }
    }
  }
`

export const PaymentSuspensionNotice = () => {
  const { isSuspended, suspensionReason } = use(CompanySuspensionContext)
  const client = useApolloClient()
  const { idCompany } = useIdCompany()
  const [isPending, startTransition] = useTransition()

  if (!isSuspended || suspensionReason !== 'payment' || !idCompany) {
    return null
  }

  const handleClick = () => {
    startTransition(async () => {
      try {
        const { data } = await client.query<
          PaymentSuspensionStripePortalQuery,
          PaymentSuspensionStripePortalQueryVariables
        >({
          query: PAYMENT_SUSPENSION_STRIPE_PORTAL_QUERY,
          variables: { idCompany },
          fetchPolicy: 'network-only',
        })

        const portalUrl = data?.company?.sevallaBilling?.stripeCustomerPortal

        if (portalUrl) {
          window.open(portalUrl, '_blank', 'noopener,noreferrer')
        } else {
          toast(
            <ToasterContent variant="error">
              Unable to open billing portal
            </ToasterContent>,
            { position: 'top-right' }
          )
        }
      } catch {
        toast(
          <ToasterContent variant="error">
            Unable to open billing portal
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <>
      <Button
        variant="destructive"
        size="sm"
        disabled={isPending}
        onClick={handleClick}
      >
        {isPending ? (
          <Loader className="animate-spin size-4" />
        ) : (
          <TriangleAlert className="size-4" />
        )}
        <span>Account is suspended — Pay now</span>
      </Button>
      <Separator orientation="vertical" className="h-4!" />
    </>
  )
}
